import React, {
  useRef,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from "@mui/material";

import {
  MapContainer,
  TileLayer,
  Polyline,
  Popup,
  Marker,
  Tooltip,
  useMapEvents,
} from "react-leaflet";
import { toast } from "react-toastify";
import { Fullscreen, FullscreenExit } from "@mui/icons-material";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";

import { Resizable } from "react-resizable";
import "react-resizable/css/styles.css";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { updateBillboardsinBulk } from "../../apis/videos.apis";

import Loader from "../../components/Loader";

const defaultCenter = [51.505, -0.09];
const defaultZoom = 13;

const createArrowIcon = (rotationAngle) =>
  L.divIcon({
    html: `<div style="
        width: 0;
        height: 0;
        border-left: 10px solid transparent;
        border-right: 10px solid transparent;
        border-bottom: 15px solid black;
        transform: rotate(${rotationAngle}deg);"></div>`,
    className: "",
    iconSize: [20, 20],
  });

const createSquareIcon = () =>
  L.divIcon({
    html: `<div style="
        width: 12px;
        height: 12px;
        background-color: black;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid white;
      "></div>`,
    className: "",
    iconSize: [20, 20],
  });

const createIcon = (color = "grey", text = "") =>
  L.divIcon({
    html: `<div style="
        background-color: ${color}; 
        color: white;
        border-radius: 50%;
        width: 25px;
        height: 25px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-weight: bold;
      ">${text}</div>`,
    className: "",
    iconSize: [25, 25],
  });

const getRotationAngle = (start, end) => {
  if (!start || !end) return 0;
  const dy = end[1] - start[1];
  const dx = end[0] - start[0];
  return (Math.atan2(dy, dx) * 180) / Math.PI;
};

const MapView = ({ videos = [], billboards = [] }) => {
  const mapRef = useRef(null);
  const [activePopup, setActivePopup] = useState(null);
  const [isLoaderOpen, setisLoaderOpen] = useState(false);
  const [mapSize, setMapSize] = useState({ width: 490, height: 400 });
  const [highlightedVideoId, setHighlightedVideoId] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [openDialog, setOpenDialog] = useState({ type: "", open: false });
  const [vehicleAllowance, setVehicleAllowance] = useState({
    two_wheeler: true,
    three_wheeler: true,
    four_wheeler: true,
    bus: true,
  });
  const [laneCount, setLaneCount] = useState(0);
  const [selectedVideoIds, setSelectedVideoIds] = useState([]);
  const toggleFullscreen = () => setIsFullscreen((prev) => !prev);

  const handleRightClick = (e, videoId) => {
    e.originalEvent.preventDefault();
    setHighlightedVideoId((prev) => (prev === videoId ? null : videoId));
  };

  const MapEventHandler = () => {
    useMapEvents({
      contextmenu: () => setHighlightedVideoId(null),
    });
    return null;
  };

  const [locationValue, setLocationValue] = useState("");
  const [trafficDirectionValue, setTrafficDirectionValue] = useState("");

  // Function to handle opening dialog
  const handleDialogOpen = (type, videoId) => {
    setOpenDialog({ type, open: true, videoId });
  };

  const handleSave = async () => {
    try {
      let payload;

      if (openDialog.type === "location") {
        payload = { location: locationValue };
      } else if (openDialog.type === "traffic_direction") {
        payload = { traffic_direction: trafficDirectionValue };
      } else if (openDialog.type === "vehicle_allowance") {
        payload = { vehicle_allowance: vehicleAllowance };
      } else if (openDialog.type === "lane_count") {
        payload = {lane_count: Number(laneCount)}
      }
      

      await updateBillboardsinBulk(selectedVideoIds, payload);

      toast.success(`Updated ${selectedVideoIds.length} videos`);
      setOpenDialog({ ...openDialog, open: false });
      setLocationValue("");
      setTrafficDirectionValue("");
      setVehicleAllowance({
        two_wheeler: true,
        three_wheeler: true,
        four_wheeler: true,
        bus: true,
      });
      setLaneCount(0);
      setSelectedVideoIds([]);
    } catch (error) {
      toast.error("Failed to update data");
    }
  };

  const lines = useMemo(() => {
    return videos
      .filter((v) => Array.isArray(v.coords) && v.coords.length > 0)
      .map((v) => {
        const validCoords = v.coords.filter(
          (c) =>
            Array.isArray(c) &&
            c.length === 2 &&
            c[0] !== undefined &&
            c[1] !== undefined
        );
        return {
          video_id: v.video_id,
          filename: v.filename,
          coords: validCoords, // already in [lat, lng] format
          speeds: Array.isArray(v.speeds)
            ? v.speeds.filter((s) => s !== undefined)
            : [],
        };
      })
      .filter((line) => line.coords.length > 0);
  }, [videos]);

  const isBillboardComplete = (billboard) => {
    // List of fields to check (add all your billboard fields here)
    const fieldsToCheck = [
      "latitude",
      "longitude",
      "location",
      "traffic_direction",
      "vendor_name",
      "media_type",
      "illumination",
      "map_image",
      "site_image",
      "total_cost",
    ];

    return fieldsToCheck.every((field) => {
      const value = billboard[field];
      return value !== null && value !== undefined && value !== "";
    });
  };

  const areAllBillboardsComplete = (videoId) => {
    const videoBillboards = billboards.filter(
      (bil) => bil.video_id === videoId
    );
    if (videoBillboards.length === 0) return false;

    return videoBillboards.every(isBillboardComplete);
  };

  useEffect(() => {
    if (!mapRef.current || lines.length === 0 || lines[0].coords.length === 0)
      return;

    const map = mapRef.current;

    // Get current map center
    const center = map.getCenter();

    // If map is at default location, then set it to first line’s starting point
    if (
      Math.abs(center.lat - defaultCenter[0]) < 0.0001 &&
      Math.abs(center.lng - defaultCenter[1]) < 0.0001
    ) {
      map.setView(lines[0].coords[0], 15); // You can change the zoom here
    }
  }, [lines]);

  const onResize = (event, { size }) => {
    setMapSize({ width: size.width, height: size.height });
  };
  console.log(lines);
  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={toggleFullscreen}
        style={{
          position: "fixed",
          top: "10px",
          right: "10px",
          zIndex: 9999,
          background: "rgba(0, 0, 0, 0.7)",
          color: "white",
          border: "none",
          cursor: "pointer",
          padding: "10px",
          borderRadius: "5px",
          display: "flex",
          alignItems: "center",
        }}
      >
        {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
      </button>

      <Resizable
        width={isFullscreen ? window.innerWidth : mapSize.width}
        height={isFullscreen ? window.innerHeight : mapSize.height}
        onResize={onResize}
        minConstraints={[300, 300]}
        maxConstraints={[2400, 2400]}
        resizeHandles={["s", "w", "e", "n", "sw", "nw", "se", "ne"]}
        style={{
          position: isFullscreen ? "fixed" : "relative",
          top: isFullscreen ? "0" : "auto",
          left: isFullscreen ? "0" : "auto",
          width: isFullscreen ? "100vw" : mapSize.width + "px",
          height: isFullscreen ? "100vh" : mapSize.height + "px",
          zIndex: isFullscreen ? 1000 : "auto",
        }}
      >
        <div
          style={{
            width: mapSize.width + "px",
            height: mapSize.height + "px",
            border: "1px solid #ccc",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <MapContainer
            center={defaultCenter}
            zoom={defaultZoom}
            ref={mapRef}
            style={{ width: "100%", height: "100%" }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            <MapEventHandler />

            {lines.map((line) => {
              const avgSpeed =
                line.speeds.reduce((acc, s) => acc + s, 0) / line.speeds.length;

              const startCoord = line.coords[0];
              const endCoord = line.coords[line.coords.length - 1];
              const secondLast = line.coords[line.coords.length - 2];
              const rotationAngle = getRotationAngle(secondLast, endCoord);

              const allComplete = areAllBillboardsComplete(line.video_id);
              const lineColor =
                highlightedVideoId === line.video_id
                  ? "black"
                  : allComplete
                  ? "green"
                  : "red";

              return (
                <React.Fragment key={line.video_id}>
                  <Polyline
                    pathOptions={{
                      color: selectedVideoIds.includes(line.video_id)
                        ? "blue"
                        : areAllBillboardsComplete(line.video_id)
                        ? "green"
                        : "red",
                      weight: 10,
                    }}
                    positions={line.coords}
                    eventHandlers={{
                      click: () => {
                        setSelectedVideoIds((prev) => {
                          if (prev.includes(line.video_id)) {
                            return prev.filter((id) => id !== line.video_id); // Deselect
                          } else {
                            return [...prev, line.video_id]; // Select
                          }
                        });
                      },
                    }}
                  >
                    <Popup>
                      <List sx={{ padding: 0 }}>
                        <ListItemButton
                          onClick={() => handleDialogOpen("location")}
                        >
                          <ListItemText primary="Set Location for selected videos" />
                        </ListItemButton>
                        <ListItemButton
                          onClick={() => handleDialogOpen("traffic_direction")}
                        >
                          <ListItemText primary="Set Traffic Direction for selected videos" />
                        </ListItemButton>
                        <ListItemButton
                          onClick={() => handleDialogOpen("vehicle_allowance")}
                        >
                          <ListItemText primary="Set Vehicle Allowance for selected videos" />
                        </ListItemButton>
                        <ListItemButton
                          onClick={() => handleDialogOpen("lane_count")}
                        >
                          <ListItemText primary="Set Lane Count for selected videos" />
                        </ListItemButton>
                      </List>
                    </Popup>
                  </Polyline>

                  <Marker position={startCoord} icon={createSquareIcon()} />
                  <Marker
                    position={endCoord}
                    icon={createArrowIcon(rotationAngle)}
                  />
                </React.Fragment>
              );
            })}

            {billboards
              .filter(
                (bil) =>
                  bil.latitude !== undefined && bil.longitude !== undefined
              )
              .map((bil) => (
                <Marker
                  key={bil.id}
                  position={[bil.latitude, bil.longitude]}
                  icon={createIcon(
                    highlightedVideoId === bil.video_id ? "black" : "grey"
                  )}
                  eventHandlers={{
                    contextmenu: (e) => handleRightClick(e, bil.video_id),
                    click: () => setActivePopup(bil),
                  }}
                >
                  <Tooltip>
                    <div>
                      <div>Tracker ID: {bil.tracker_id}</div>
                      <div>Visibility Duration: {bil.visibility_duration}</div>
                    </div>
                  </Tooltip>
                </Marker>
              ))}
          </MapContainer>
        </div>
      </Resizable>
      <Loader open={isLoaderOpen} />

      <Dialog
        open={openDialog.open && openDialog.type === "location"}
        onClose={() => setOpenDialog({ ...openDialog, open: false })}
      >
        <DialogTitle>
          {" "}
          Set{" "}
          {openDialog.type === "location"
            ? "Location"
            : "Traffic Direction"}{" "}
          for {selectedVideoIds.length} videos
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Location"
            type="text"
            fullWidth
            variant="standard"
            value={locationValue}
            onChange={(e) => setLocationValue(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog({ ...openDialog, open: false })}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Traffic Direction Dialog */}
      <Dialog
        open={openDialog.open && openDialog.type === "traffic_direction"}
        onClose={() => setOpenDialog({ ...openDialog, open: false })}
      >
        <DialogTitle>
          Set{" "}
          {openDialog.type === "location" ? "Location" : "Traffic Direction"}{" "}
          for {selectedVideoIds.length} videos
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Traffic Direction"
            type="text"
            fullWidth
            variant="standard"
            value={trafficDirectionValue}
            onChange={(e) => setTrafficDirectionValue(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog({ ...openDialog, open: false })}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openDialog.open && openDialog.type === "vehicle_allowance"}
        onClose={() => setOpenDialog({ ...openDialog, open: false })}
      >
        <DialogTitle>
          Set Vehicle Allowance for {selectedVideoIds.length} videos
        </DialogTitle>
        <DialogContent>
          {["two_wheeler", "three_wheeler", "four_wheeler", "bus"].map(
            (type) => (
              <div
                key={type}
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <input
                  type="checkbox"
                  checked={vehicleAllowance[type]}
                  onChange={(e) =>
                    setVehicleAllowance((prev) => ({
                      ...prev,
                      [type]: e.target.checked,
                    }))
                  }
                  id={type}
                />
                <label
                  htmlFor={type}
                  style={{ marginLeft: 8, textTransform: "capitalize" }}
                >
                  {type.replace("_", " ")}
                </label>
              </div>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog({ ...openDialog, open: false })}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openDialog.open && openDialog.type === "lane_count"}
        onClose={() => setOpenDialog({ ...openDialog, open: false })}
        >
       <DialogTitle>
          Set Lane Count for {selectedVideoIds.length} videos
        </DialogTitle>
         <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Lane Count"
            type="number"
            fullWidth
            variant="standard"
            value={laneCount}
            onChange={(e) => setLaneCount(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog({ ...openDialog, open: false })}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MapView;
